import ClockoutController from '../../../core/clockout.controller';
import Inherit from '../../../core/inherit';
import HeaderDelegate from './header.delegate';
import { HeaderConstants, HEADER_PAGES } from './header.constants';
import { formatUnreadBadge } from './header.utils';
import { navbarSections } from './navbar.constants';
import { NAVBAR_ACTIVE_PATH_ALIASES } from './navbar.paths';
import { SCOPES } from '../../../constants/scopes.constants';
import { decrementUnread, getUnreadSnapshot, setUnreadCount, subscribeToUnread } from '../../../data/notifications.store';
import { getBreadcrumbFor, getBreadcrumbSnapshot, subscribeToBreadcrumb } from '../../../data/breadcrumb.store';
import { resolveNotificationDestination } from '../../../utils/notifications';

class HeaderController extends Inherit(ClockoutController, HeaderDelegate) {
    state = {
        drawerOpen: false,
        breadcrumbSnapshot: getBreadcrumbSnapshot(),
        unreadCount: getUnreadSnapshot().count,
        notificationsOpen: false,
        menuItems: [],
        menuLoading: false,
        menuError: false,
    };

    async initialize() {
        // componentDidMount awaits fetchUserByToken() before calling us; if the
        // header unmounted meanwhile, cleanup() already ran and nothing may start.
        if (this._unmounted) return;

        // Pages publish deeper trails to breadcrumb.store (keyed by pathname).
        this._unsubscribeBreadcrumb = subscribeToBreadcrumb((snapshot) => this.setBreadcrumbSnapshot(snapshot));
        this.setBreadcrumbSnapshot(getBreadcrumbSnapshot());

        if (!this.shouldShowNotifications()) return;

        // Header remounts on every route change, so the count + last-fetch time
        // live in notifications.store: render the cached value now and only hit
        // the API when the 60s interval has actually elapsed.
        this._unsubscribeUnread = subscribeToUnread((snapshot) => this.setUnreadCount(snapshot.count));
        this._pollToken = (this._pollToken || 0) + 1;

        const snapshot = getUnreadSnapshot();
        const age = snapshot.fetchedAt ? Date.now() - snapshot.fetchedAt : Infinity;
        if (age >= HeaderConstants.UNREAD_POLL_INTERVAL_MS) {
            this.pollUnreadCount(this._pollToken);
        } else {
            this.setUnreadCount(snapshot.count);
            this.scheduleUnreadPoll(this._pollToken, HeaderConstants.UNREAD_POLL_INTERVAL_MS - age);
        }
    }

    async cleanup() {
        // Invalidate any in-flight poll or menu fetch, drop the timer, and stop
        // listening — the header must leave nothing running behind it.
        this._unmounted = true;
        this._pollToken = (this._pollToken || 0) + 1;
        this._menuToken = (this._menuToken || 0) + 1;
        if (this._unreadTimer) clearTimeout(this._unreadTimer);
        this._unreadTimer = null;
        this._unsubscribeUnread?.();
        this._unsubscribeUnread = null;
        this._unsubscribeBreadcrumb?.();
        this._unsubscribeBreadcrumb = null;
    }

    scheduleUnreadPoll(token, delayMs = HeaderConstants.UNREAD_POLL_INTERVAL_MS) {
        if (this._unreadTimer) clearTimeout(this._unreadTimer);
        this._unreadTimer = setTimeout(() => this.pollUnreadCount(token), delayMs);
    }

    /** setTimeout chained after each response (never setInterval) so slow requests can't overlap. */
    async pollUnreadCount(token) {
        if (token !== this._pollToken) return;
        const count = await this.data.fetchNotificationsUnreadCount(); // never throws; null on failure
        if (token !== this._pollToken) return;
        if (count != null) setUnreadCount(count); // store → subscription → state
        this.scheduleUnreadPoll(token);
    }

    /** Bell + menu are gated by the same scope the /notifications Page checks. */
    shouldShowNotifications() {
        return this.hasScope(SCOPES.SUPPORT_VIEW);
    }

    handleNotificationsOpenChange(open) {
        this.setNotificationsOpen(open);
        if (open) this.loadMenuItems();
    }

    /** Fetch the latest five unread for the menu; errors render inline, never throw. */
    async loadMenuItems() {
        const token = (this._menuToken = (this._menuToken || 0) + 1);
        this.setMenuLoading(true);
        this.setMenuError(false);
        try {
            const data = await this.data.fetchNotifications({
                page: 1,
                perPage: HeaderConstants.NOTIFICATIONS_MENU_SIZE,
                unread: true,
            });
            if (token !== this._menuToken) return;
            this.setMenuItems(data?.items || []);
        } catch (error) {
            if (token !== this._menuToken) return;
            console.error('Error loading notifications menu:', error);
            this.setMenuItems([]);
            this.setMenuError(true);
        } finally {
            if (token === this._menuToken) this.setMenuLoading(false);
        }
    }

    /** Same contract as a Notifications screen row: mark read, close, deep-link. */
    handleMenuItemClick(item) {
        if (!item?.id) return;
        if (!item.readAt) {
            this.markMenuItemRead(item.id);
            decrementUnread(1);
            this.data.markNotificationsRead([item.id]).catch(() => { /* non-blocking; badge resyncs on the next poll */ });
        }
        this.setNotificationsOpen(false);
        const destination = resolveNotificationDestination(item);
        if (destination?.kind === 'ticket') this.navigateTicket(destination.id);
        else if (destination?.kind === 'user') this.navigateUser(destination.id);
    }

    handleViewAllClick() {
        this.setNotificationsOpen(false);
        this.navigateNotifications();
    }

    handleSupportClick = () => {
        this.navigateSupport();
    };

    handleMenuClick = () => {
        this.setDrawerOpen(true);
    };

    handleDrawerClose = () => {
        this.setDrawerOpen(false);
    };

    handleNavigate(path) {
        if (!path) return;
        this.setDrawerOpen(false);
        if (this.props.navigate) {
            this.props.navigate(path);
        }
    }

    /**
     * Label of the page at `pathname`: legacy routes go through the sidebar's
     * active-path aliases, then the longest sidebar or header page whose path
     * is the pathname or a parent of it (/support/ticket/9 → Support).
     *
     * @param {string} pathname
     * @returns {string|null}
     */
    resolvePageLabel(pathname) {
        if (!pathname) return null;
        const path = NAVBAR_ACTIVE_PATH_ALIASES[pathname] ?? pathname;
        const match = [...navbarSections, ...HEADER_PAGES]
            .filter((page) => page.path && page.label && (path === page.path || path.startsWith(`${page.path}/`)))
            .sort((a, b) => b.path.length - a.path.length)[0];
        return match ? match.label : null;
    }

    /**
     * Breadcrumb steps for the current route: the trail the page published to
     * breadcrumb.store for this pathname, else the page's own label.
     *
     * @returns {Array<object>}
     */
    getBreadcrumbSteps() {
        const pathname = this.props.location?.pathname || '';
        const published = getBreadcrumbFor(pathname, this.state.breadcrumbSnapshot);
        if (published) {
            return published;
        }
        const label = this.resolvePageLabel(pathname);
        return label ? [{ key: 'page', label }] : [];
    }

    handleBreadcrumbNavigate(path) {
        if (path && this.props.navigate) {
            this.props.navigate(path);
        }
    }

    /**
     * Account menu meta line: "Role · Bank" (Figma «Navbar» popover). `null` when
     * the user has no role, so the line shows only the bank: `GET /api/user` sends
     * no role yet, and `DEFAULT_ROLE` is RoleGuard's least-privilege fallback, not
     * a role to show.
     */
    getAccountRole() {
        return this.getHeaderUser()?.role ?? null;
    }

    getHeaderUser() {
        return this.props.user ?? this.data?.user;
    }

    getHeaderFi() {
        return this.props.fi ?? this.getHeaderUser()?.fi?.name;
    }

    handleLogoutClick = () => {
        this.logout();
    };

    hasNotifications() {
        return this.state.unreadCount > 0;
    }

    /** Unread count for the bell's accessible name — null when nothing is unread, "99+" above the cap. */
    getUnreadBadge() {
        return formatUnreadBadge(this.state.unreadCount, HeaderConstants.UNREAD_BADGE_MAX);
    }

    getNotificationsLabel() {
        const badge = this.getUnreadBadge();
        return badge ? `Notifications, ${badge} unread` : 'Notifications';
    }

    shouldShowSupport() {
        return this.hasScope(SCOPES.SUPPORT_VIEW);
    }
}

export default HeaderController;
